'use client';

import React from 'react';
import { Menu, MenuItem } from './DevDropdown';
import ExchangeHistoryClient from '../../utils/exchangeHistoryClient';
import { MarketType } from '@velocity-exchange/sdk';
import { ChevronDown } from '@velocity-exchange/icons';
import { notify } from 'src/utils/notifications';
import { useSwitchMarket } from 'src/hooks/driftActions/market/useSwitchMarket';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { DriftWindow } from 'src/window/driftWindow';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import useForceUpdate from '../../hooks/useForceUpdate';
import useAlertNotify from 'src/components/Toasts/AlertNotifications/hooks/useAlertNotify';
import useModal from 'src/components/Modals/hooks/useModal';
import { getColorVar } from 'src/utils/theme/colorVars';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useUserAccountIsReady from 'src/hooks/useUserAccountIsReady';
import { ZERO } from '@velocity-exchange/sdk';
import { UIMarket } from '@velocity-exchange/common';
import {
	setDevLiquidationOverride,
	clearDevLiquidationOverride,
	hasDevLiquidationOverride,
} from 'src/utils/dev/devLiquidationOverrides';

const BASIC_NOTIFICATION_COUNT_REF = { current: 0 };

const DevTopDropDown = () => {
	const { switchMarket } = useSwitchMarket();
	const triggerRefresh = useForceUpdate();
	const { show } = useModal();
	const alertNotify = useAlertNotify();
	const getState = useDriftStore((s) => s.get);

	const [preflightEnabled, setPreflightEnabled] = React.useState(true);
	const [, setOverrideRefresh] = React.useState(0);

	const driftClient = useDriftClient();
	const isDriftClientReady = useDriftClientIsReady();
	const isUserAccountReady = useUserAccountIsReady();
	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const userClient =
		isDriftClientReady && isUserAccountReady && driftClient?.hasUser()
			? driftClient.getUser()
			: undefined;

	const marginCalc = React.useMemo(() => {
		return userClient && isUserAccountReady
			? userClient.getMarginCalculation('Maintenance')
			: undefined;
	}, [userClient, isUserAccountReady]);

	const isolatedPositions = React.useMemo(() => {
		if (!marginCalc) return [];
		const positions: Array<{ marketIndex: number; label: string }> = [];
		marginCalc.isolatedMarginCalculations.forEach((iso, marketIndex) => {
			if (iso.marginRequirement.gt(ZERO)) {
				const ui = UIMarket.createPerpMarket(marketIndex).uiSymbols;
				positions.push({
					marketIndex,
					label: `${ui.marketDisplaySymbol} (Isolated)`,
				});
			}
		});
		return positions;
	}, [marginCalc]);

	// Listen for override changes to update menu
	React.useEffect(() => {
		const interval = setInterval(() => {
			setOverrideRefresh((prev) => prev + 1);
		}, 500); // Check every 500ms for override state changes

		return () => clearInterval(interval);
	}, []);

	const showEmulationModal = () => {
		show({ type: 'emulation', props: {} });
	};

	return (
		<Menu
			className="absolute top-2xs left-0 h-[10px] w-[30px]"
			label={<ChevronDown color={getColorVar('text-secondary')} size={24} />}
		>
			<MenuItem
				label="Configure Feature Flags"
				onClick={() => {
					show({ type: 'feature_flag', props: {} });
				}}
			/>
			<MenuItem
				label="Privy Dev"
				onClick={() => {
					show({ type: 'privy_dev', props: {} });
				}}
			/>
			<Menu label="Misc. Actions">
				{/* Force liquidation alert */}
				<MenuItem
					label="Show liquidation alert for current account"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('force_liquidation_alert');
					}}
				/>
				{/* Mark isolated position as liquidated */}
				{isolatedPositions.length > 0 && currentUserKey && (
					<Menu label="Mark Isolated Position as Liquidated">
						{isolatedPositions.map(({ marketIndex, label }) => {
							const isOverridden = hasDevLiquidationOverride(
								currentUserKey,
								marketIndex
							);
							return (
								<MenuItem
									key={marketIndex}
									label={`${isOverridden ? '✓ ' : ''}${label}`}
									onClick={() => {
										if (isOverridden) {
											clearDevLiquidationOverride(currentUserKey, marketIndex);
										} else {
											setDevLiquidationOverride(
												currentUserKey,
												marketIndex,
												30_000
											);
										}
										setOverrideRefresh((prev) => prev + 1);
										triggerRefresh();
									}}
								/>
							);
						})}
					</Menu>
				)}
				{/* Drigger token account sync */}
				<MenuItem
					label="Trigger token account sync"
					onClick={() => {
						useDriftStore
							.getState()
							.appEventEmitter.emit('refreshTokenAccounts');
					}}
				/>
				{/* Useful to access onboarding steps */}
				<MenuItem
					label={`${
						preflightEnabled ? 'Disable' : 'Enable'
					} transaction preflight`}
					onClick={() => {
						// If preflight is currently enabled, then we are flipping it to disabled (skip preflight = true)
						const shouldSkipPreflight = preflightEnabled;
						const driftClient = getState().driftClient;
						if (driftClient?.client?.opts) {
							driftClient.client.opts.skipPreflight = shouldSkipPreflight;
						}

						setPreflightEnabled(!preflightEnabled);

						triggerRefresh();
					}}
				/>
				<MenuItem
					label="Toggle Force Stale Data Warning"
					onClick={() => {
						DriftWindow.FORCE_DISPLAY_STALE_PAGE_WARNING =
							!DriftWindow.FORCE_DISPLAY_STALE_PAGE_WARNING;
					}}
				/>
				<MenuItem
					label="Clear Wallet Memory"
					onClick={() => {
						localStorage.removeItem('hasConnectedWalletBefore');
						localStorage.removeItem('lastAcknowledgedTerms');
					}}
				/>
				<MenuItem
					label="Withdraw all dust"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('withdraw_all_dust');
					}}
				/>
				<MenuItem
					label="Withdraw 5 USDC"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('withdraw_5_usdc');
					}}
				/>
				<MenuItem
					label="Reclaim Rent"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('reclaim_rent');
					}}
				/>
				<MenuItem
					label="Create anchor error tx"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('force_anchor_error_tx');
					}}
				/>
				<MenuItem
					label="Create drift error tx"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('force_drift_error_tx');
					}}
				/>
				<MenuItem
					label="Create insufficient solana wallet balance error"
					onClick={() => {
						DriftWindow.devEventEmitter.emit(
							'force_insufficient_wallet_balance_error'
						);
					}}
				/>
				<MenuItem
					label="Fetch top makers"
					onClick={async () => {
						const result =
							await ExchangeHistoryClient.getTopMakersKeysAndAccounts({
								marketIndex: 0,
								marketType: MarketType.PERP,
								side: 'bid',
								limit: 2,
							});

						console.log(result);
					}}
				/>
				<MenuItem
					label="Create basic notification"
					onClick={() => {
						notify({
							type: 'info',
							message: `Basic Notification ${BASIC_NOTIFICATION_COUNT_REF.current++}`,
							id: Math.random().toString(),
							showUntilCancelled: true,
						});
					}}
				/>
				<MenuItem
					label="Create basic notification with long message"
					onClick={() => {
						notify({
							type: 'info',
							message: `Basic Notification with a really long message that should wrap.`,
							id: Math.random().toString(),
							showUntilCancelled: true,
						});
					}}
				/>
				<MenuItem
					label="Create awaiting notification"
					onClick={() => {
						notify({
							type: 'awaiting',
							message: `Awaiting Notification`,
							id: Math.random().toString(),
							showUntilCancelled: true,
						});
					}}
				/>
				<MenuItem
					label="Create timeout notification"
					onClick={() => {
						notify({
							type: 'timeout',
							timeoutStartMs: Date.now(),
							timeoutEndMs: Date.now() + 30_000,
							message: `Timeout Notification`,
							id: Math.random().toString(),
							showUntilCancelled: true,
						});
					}}
				/>
				<MenuItem
					label="Create random notification"
					onClick={() => {
						notify({
							// @ts-ignore
							type: ['success', 'error', 'warning', 'info'][
								Math.floor(Math.random() * 4)
							],
							message: `Random Notification`,
							description: `This is a description`,
							subDescription:
								Math.random() > 0.5 ? `This is a sub description` : undefined,
							id: Math.random().toString(),
							// lengthMs: 1000_000 * Math.random(),
							lengthMs: 30_000,
							action: {
								type: 'txnLink',
								txnSig: 'random_tx_sig',
							},
							showUntilCancelled: true,
						});
					}}
				/>
				<MenuItem
					label="Create minor alert notification"
					onClick={() => {
						alertNotify({
							alertType: 'minor',
							message: 'Minor Alert Notification',
							description: 'This is a minor alert.',
							id: Math.random().toString(),
							disableAlertAutoDismiss: Math.random() > 0.5,
							action:
								Math.random() > 0.5
									? {
											type: 'link',
											label: 'Trade SOL',
											href: '/SOL-PERP',
										}
									: {
											type: 'button',
											label: 'Trade SOL',
											callback: () => {
												switchMarket({
													marketIndex: 0,
													marketType: MarketType.PERP,
												});
											},
										},
						});
					}}
				/>
				<MenuItem
					label="Create major alert notification"
					onClick={() => {
						alertNotify({
							alertType: 'major',
							message: 'Major Alert Notification',
							imageSrc: '/assets/images/drift-earn/amplify-feature-alert.png',
							description: 'This is a major alert.',
							id: Math.random().toString(),
							disableAlertAutoDismiss: Math.random() > 0.5,
							action:
								Math.random() > 0.5
									? {
											type: 'link',
											label: 'Trade SOL',
											href: '/SOL-PERP',
										}
									: {
											type: 'button',
											label: 'Trade SOL',
											callback: () => {
												switchMarket({
													marketIndex: 0,
													marketType: MarketType.PERP,
												});
											},
										},
						});
					}}
				/>
				<MenuItem
					label="Emulate Account (with current signer)"
					onClick={showEmulationModal}
				/>
				<MenuItem
					label="Enable Logging Service"
					onClick={() => {
						DriftWindow.enableLoggingService();
					}}
				/>
				<MenuItem
					label="Log feature flag state"
					onClick={() => {
						console.log(useFeatureFlagStore.getState());
					}}
				/>
				<MenuItem
					label="Refresh chart candles"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('refresh_candle_history');
					}}
				/>
				<MenuItem
					label="Simulate Orderbook WS Failure (15s)"
					onClick={() => {
						console.log(
							'🧪 DEV: Simulating orderbook websocket failure for 15s...'
						);
						(window as any).DRIFT_DEV_BLOCK_WEBSOCKET_MESSAGES = true;

						// Don't restart — let the pipeline timeout naturally (WS_TIMEOUT_MS),
						// which triggers fallback to polling

						setTimeout(() => {
							console.log('🧪 DEV: Re-enabling orderbook websocket messages');
							(window as any).DRIFT_DEV_BLOCK_WEBSOCKET_MESSAGES = false;
							// Force recovery to reconnect websocket
							(DriftWindow.devEventEmitter as any).emit(
								'restart_orderbook_subscription'
							);
						}, 15000);
					}}
				/>
				<MenuItem
					label="Simulate Pricing WS Failure (15s)"
					onClick={() => {
						console.log(
							'🧪 DEV: Simulating pricing websocket failure for 15s...'
						);
						// Block new websocket messages from being processed
						(window as any).DRIFT_DEV_BLOCK_PRICING_WEBSOCKET_MESSAGES = true;

						// Trigger proper fallback flow (destroys client, sets timestamp, switches to polling)
						DriftWindow.devEventEmitter.emit('simulate_pricing_ws_failure');

						// Re-enable messages after 15s - recovery will happen automatically after 30s cooldown
						setTimeout(() => {
							console.log('🧪 DEV: Re-enabling pricing websocket messages');
							(window as any).DRIFT_DEV_BLOCK_PRICING_WEBSOCKET_MESSAGES =
								false;
						}, 15000);
					}}
				/>
			</Menu>
			<Menu label="Posthog">
				<MenuItem
					label="Enable Session Recording"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('set_session_recording', true);
					}}
				/>
				<MenuItem
					label="Disable Session Recording"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('set_session_recording', false);
					}}
				/>
				<MenuItem
					label="Enable Event Capture"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('set_event_capture', true);
					}}
				/>
				<MenuItem
					label="Disable Event Capture"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('set_event_capture', false);
					}}
				/>
				<MenuItem
					label={`${
						DriftWindow.forceInvariantCheckFailure ? 'Disable' : 'Enable'
					} FORCE invariant check failure`}
					onClick={() => {
						DriftWindow.forceInvariantCheckFailure =
							!DriftWindow.forceInvariantCheckFailure;
						triggerRefresh();
					}}
				/>
			</Menu>
			<Menu label="Performance">
				<MenuItem
					label="Profile for 5 secs"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('profile_for_5_secs');
					}}
				/>
				<MenuItem
					label="Start Profiling"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('start_profiling');
					}}
				/>
				<MenuItem
					label="Stop Profiling"
					onClick={() => {
						DriftWindow.devEventEmitter.emit('stop_profiling');
					}}
				/>
			</Menu>
		</Menu>
	);
};

export default DevTopDropDown;
